/**
 * Shared DEV dungeon level + mutation pickers (Gate players never see level).
 * Supports DL 1–100 with ±1 / ±10 steppers (not a 100-row list).
 */
import type Phaser from 'phaser';
import {
  DUNGEON_LEVEL_INITIAL_PLAYABLE_RANGE,
  DUNGEON_MUTATIONS,
  describeProgressionChallenge,
  type DungeonMutationId,
} from '../../data/dungeonProgression';

export const MUTATION_LABELS: Record<DungeonMutationId, string> = {
  standard: 'Standard',
  hard: 'Hard',
  brutal: 'Brutal',
};

export interface DungeonProgressionUiState {
  dungeonLevel: number;
  mutation: DungeonMutationId;
}

export function clampDevDungeonLevel(level: number): number {
  return Math.min(
    DUNGEON_LEVEL_INITIAL_PLAYABLE_RANGE.max,
    Math.max(DUNGEON_LEVEL_INITIAL_PLAYABLE_RANGE.min, Math.floor(level)),
  );
}

/**
 * Renders −10 / − / level / + / +10 and Standard/Hard/Brutal chips.
 * Returns the Y below the control and a live state object.
 */
export function addDungeonProgressionDevControls(
  scene: Phaser.Scene,
  cx: number,
  y: number,
  initial: DungeonProgressionUiState = {
    dungeonLevel: 1,
    mutation: 'standard',
  },
  options: { addToContainer?: Phaser.GameObjects.Container } = {},
): { state: DungeonProgressionUiState; nextY: number } {
  const state: DungeonProgressionUiState = {
    dungeonLevel: clampDevDungeonLevel(initial.dungeonLevel),
    mutation: initial.mutation,
  };
  const root = options.addToContainer;

  const track = (obj: Phaser.GameObjects.GameObject): void => {
    if (root) root.add(obj);
  };

  const caption = scene.add
    .text(cx, y, 'Dungeon level + mutation (DEV only)', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      color: '#b07a3a',
    })
    .setOrigin(0.5);
  track(caption);
  y += 28;

  const mkBtn = (
    x: number,
    label: string,
    w: number,
    onClick: () => void,
  ): void => {
    const bg = scene.add
      .rectangle(x, y, w, 36, 0x2a3350)
      .setStrokeStyle(1, 0xc9a227)
      .setInteractive({ useHandCursor: true });
    track(bg);
    const text = scene.add
      .text(x, y, label, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#e8d5a3',
      })
      .setOrigin(0.5);
    track(text);
    bg.on('pointerup', onClick);
  };

  const levelLabel = scene.add
    .text(cx, y, `Lv ${state.dungeonLevel}`, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '15px',
      color: '#f2e6c8',
      fontStyle: 'bold',
    })
    .setOrigin(0.5);
  track(levelLabel);

  const bump = (delta: number): void => {
    state.dungeonLevel = clampDevDungeonLevel(state.dungeonLevel + delta);
    updateHint();
  };

  mkBtn(cx - 120, '−10', 44, () => bump(-10));
  mkBtn(cx - 70, '−', 40, () => bump(-1));
  mkBtn(cx + 70, '+', 40, () => bump(1));
  mkBtn(cx + 120, '+10', 44, () => bump(10));

  y += 44;
  const gap = 108;
  const startX = cx - gap;
  const mutRefreshers: Array<() => void> = [];
  DUNGEON_MUTATIONS.forEach((id, i) => {
    const x = startX + i * gap;
    const bg = scene.add
      .rectangle(x, y, 96, 36, 0x2a3350)
      .setStrokeStyle(1, 0xc9a227)
      .setInteractive({ useHandCursor: true });
    track(bg);
    const label = scene.add
      .text(x, y, MUTATION_LABELS[id], {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#e8d5a3',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    track(label);
    const refresh = (): void => {
      const active = state.mutation === id;
      bg.setFillStyle(active ? 0x3a4a28 : 0x2a3350);
      bg.setStrokeStyle(2, active ? 0xc9a227 : 0x6e7694);
      label.setColor(active ? '#f2e6c8' : '#9aa3c0');
    };
    mutRefreshers.push(refresh);
    bg.on('pointerup', () => {
      state.mutation = id;
      updateHint();
      for (const fn of mutRefreshers) fn();
    });
    refresh();
  });

  y += 34;
  const hint = scene.add
    .text(cx, y, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '11px',
      color: '#9aa3c0',
      align: 'center',
      wordWrap: { width: 340 },
    })
    .setOrigin(0.5);
  track(hint);

  const updateHint = (): void => {
    levelLabel.setText(`Lv ${state.dungeonLevel}`);
    const copy = describeProgressionChallenge(state);
    hint.setText(`${copy.challenge} · ${copy.rewards}`);
  };
  updateHint();

  return { state, nextY: y + 28 };
}
